///
/// EarthBrowser
///
/// Created by Matt Giger on 2/22/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type DatasetFrame } from "@/earthbrowser/datasets/DatasetSeries"
import { datasetUrl } from "@/utils/datasetUrl"
import { type timerange } from "@stovokor/math/time"

export interface AuroraArrowData {
	forecast_time: string
	data_url: string
	min_value: number
	max_value: number
	vertex_count: number
	grid_resolution: number
}

export class HexGridDataFrame implements DatasetFrame {
	public url: string
	public range: timerange
	public vertexData?: Uint8Array
	public minValue = 0.0
	public maxValue = 8.0
	public multiplier = 10.0
	public loading: boolean = false
	public loaded: boolean = false
	private arrowData?: AuroraArrowData

	constructor(range: timerange, multiplier: number, forecastTime: string = "") {
		this.range = range
		this.multiplier = multiplier
		this.url = forecastTime
	}

	public setArrowData(data: AuroraArrowData): void {
		this.arrowData = data
		this.minValue = data.min_value
		this.maxValue = data.max_value
	}

	async load(): Promise<void> {
		if (this.loaded || this.loading) {
			return
		}

		if (!this.arrowData) return

		this.loading = true
		try {
			const res = await fetch(datasetUrl(this.arrowData.data_url))
			if (!res.ok) {
				throw new Error(`Failed to fetch aurora data: ${res.status}`)
			}

			const gzippedBytes = new Uint8Array(await res.arrayBuffer())

			const stream = new Response(gzippedBytes).body?.pipeThrough(
				new DecompressionStream('gzip')
			)
			const decompressedResponse = await new Response(stream).arrayBuffer()
			this.vertexData = new Uint8Array(decompressedResponse)

			this.loaded = true
		} catch (error) {
			console.error('Failed to load aurora data:', error)
			this.loaded = false
		} finally {
			this.loading = false
		}
	}

	public purge(): void {
		this.loaded = false
		this.vertexData = undefined
	}
}